// @ts-nocheck

import { useMemo } from "react";
import * as turf from "@turf/turf";
import {
  PinData,
  EnhancedPinData,
  Stops,
  ColoredLines,
  MapData,
} from "../../../src/@types/mapTypes";

const isValidCoordinate = (point: PinData): boolean => {
  return point.latitude !== 0 && point.longitude !== 0;
};

const sortByDateTime = (points: PinData[]): PinData[] => {
  return [...points].sort(
    (a, b) => new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime()
  );
};

export function useMapData(mapData: MapData) {
  return useMemo(() => {
    // Inicialização dos arrays vazios para resultados
    const stops: Stops[] = [];
    const greenLines: ColoredLines[] = [];
    const redLines: ColoredLines[] = [];
    const yellowLines: ColoredLines[] = [];
    let currentStopStart: PinData | null = null;

    // Se não tiver dados, retorna arrays vazios
    if (!mapData?.allPings || mapData.allPings.length === 0) {
      return {
        markersWithDirection: [],
        stopPoints: [],
        coloredTrajectoryLines: greenLines,
        orangeTrajectoryLines: redLines,
        yellowTrajectoryLines: yellowLines,
      };
    }

    const allPings = sortByDateTime(mapData.allPings.filter(isValidCoordinate));

    const pingsInsideClockIns = sortByDateTime(
      (mapData.pingsInsideClockIns || []).filter(isValidCoordinate)
    );

    const pingsOutsideClockIns = sortByDateTime(
      (mapData.pingsOutsideClockIns || []).filter(isValidCoordinate)
    );

    // Processar linhas para pings dentro das batidas (verde)
    for (let i = 1; i < pingsInsideClockIns.length; i++) {
      const currentPoint = pingsInsideClockIns[i];
      const prevPoint = pingsInsideClockIns[i - 1];

      greenLines.push({
        positions: [
          [prevPoint.latitude, prevPoint.longitude],
          [currentPoint.latitude, currentPoint.longitude],
        ],
        color: "green",
      });

      // Processar pontos de parada (apenas para pings dentro das batidas)
      if (currentPoint.dist === 0) {
        if (!currentStopStart) {
          currentStopStart = currentPoint;
        }
      } else if (currentStopStart) {
        stops.push({
          start: currentStopStart,
          end: prevPoint,
          duration:
            (new Date(prevPoint.data_hora).getTime() -
              new Date(currentStopStart.data_hora).getTime()) /
            60000,
        });
        currentStopStart = null;
      }
    }

    // Processar linhas para pings fora das batidas (vermelho)
    for (let i = 1; i < pingsOutsideClockIns.length; i++) {
      const currentPoint = pingsOutsideClockIns[i];
      const prevPoint = pingsOutsideClockIns[i - 1];

      redLines.push({
        positions: [
          [prevPoint.latitude, prevPoint.longitude],
          [currentPoint.latitude, currentPoint.longitude],
        ],
        color: "red",
      });
    }

    // Processar linhas para todos os pings (amarelo)
    for (let i = 1; i < allPings.length; i++) {
      const currentPoint = allPings[i];
      const prevPoint = allPings[i - 1];

      yellowLines.push({
        positions: [
          [prevPoint.latitude, prevPoint.longitude],
          [currentPoint.latitude, currentPoint.longitude],
        ],
        color: "yellow",
      });
    }

    // Calcular direção para todos os pontos
    const markers: EnhancedPinData[] = allPings.map((point: PinData, index: number) => {
      if (index === 0) {
        return { ...point, rotation: 0, distance: 0, totalDistance: 0 };
      }

      const prevPoint = allPings[index - 1];
      const start = turf.point([prevPoint.longitude, prevPoint.latitude]);
      const end = turf.point([point.longitude, point.latitude]);
      const bearing = turf.bearing(start, end);

      return {
        ...point,
        rotation: bearing,
        distance: turf.distance(start, end, { units: "kilometers" }),
        totalDistance: 0,
      };
    });

    return {
      markersWithDirection: markers,
      stopPoints: stops,
      coloredTrajectoryLines: greenLines,
      orangeTrajectoryLines: redLines,
      yellowTrajectoryLines: yellowLines,
    };
  }, [mapData]);
}
